import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk';

interface TossPaymentWidgetProps {
  amount: number;
  orderId: string;
  orderName: string;
  onSuccess: (paymentKey: string, orderId: string, amount: number) => void;
  onError: (error: string) => void;
  worshipId?: string;
}

// v2 SDK 사용: window.TossPayments 스크립트 주입 대신 SDK 모듈을 사용합니다.

const TossPaymentWidget: React.FC<TossPaymentWidgetProps> = ({
  amount,
  orderId,
  orderName,
  onSuccess,
  onError,
  worshipId
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const paymentMethodsContainerIdRef = useRef<string>(`payment-methods-${Math.random().toString(36).slice(2)}`);
  const agreementContainerIdRef = useRef<string>(`agreement-${Math.random().toString(36).slice(2)}`);
  const widgetsRef = useRef<any | null>(null);
  const paymentMethodsWidgetRef = useRef<any | null>(null);
  const agreementWidgetRef = useRef<any | null>(null);
  const isRequestingRef = useRef<boolean>(false);

  const clientKey = (process.env.REACT_APP_TOSS_CLIENT_KEY || '').trim();
  const brandpayRedirectPath = (process.env.REACT_APP_TOSS_BRANDPAY_REDIRECT_PATH || '').trim();
  const variantKey = (process.env.REACT_APP_TOSS_VARIANT_KEY || 'DEFAULT').trim();
  const agreementVariantKey = (process.env.REACT_APP_TOSS_AGREEMENT_VARIANT_KEY || 'AGREEMENT').trim();

  useEffect(() => {
    // Capture ref values at the time of effect execution for cleanup function
    const pmId = paymentMethodsContainerIdRef.current;
    const agId = agreementContainerIdRef.current;

    // Debug: Verify injected client key at runtime
    console.log('TOSS CK prefix:', (clientKey || '').slice(0, 7)); // 기대: "test_ck" 또는 "live_ck"
    console.log('TOSS CK length:', (clientKey || '').length);      // 기대: 충분히 긴 길이
    const initPaymentWidget = async () => {
      try {
        if (widgetsRef.current) {
          console.log('TOSS: widgets already initialized - skipping');
          return;
        }
        setIsLoading(true);

        // TossPayments SDK 초기화 후 widgets 인스턴스 생성 (공식 샘플 패턴)
        const tossPayments = await loadTossPayments(clientKey);
        const widgetsInitOptions: any = { customerKey: ANONYMOUS };
        if (brandpayRedirectPath) {
          widgetsInitOptions.brandpay = {
            redirectUrl: `${window.location.origin}${brandpayRedirectPath}`
          };
        }
        const widgets = tossPayments.widgets(widgetsInitOptions);
        widgetsRef.current = widgets;

        // 로딩 해제하여 컨테이너 DOM이 렌더되도록 유도
        setIsLoading(false);
        // 다음 렌더 사이클까지 대기하여 selector 대상이 DOM에 존재하도록 보장
        await new Promise((resolve) => setTimeout(resolve, 0));

        // 결제 금액 설정은 렌더링보다 선행되어야 함
        await widgets.setAmount({ currency: 'KRW', value: amount });

        // 결제수단 UI 렌더링
        console.log('TOSS variantKey (payment):', variantKey);
        paymentMethodsWidgetRef.current = await widgets.renderPaymentMethods({
          selector: `#${pmId}`,
          variantKey
        });

        // 약관 동의 UI 렌더링
        console.log('TOSS variantKey (agreement):', agreementVariantKey);
        agreementWidgetRef.current = await widgets.renderAgreement({
          selector: `#${agId}`,
          variantKey: agreementVariantKey
        });
      } catch (err) {
        console.error('토스페이먼츠 위젯 초기화 오류:', err);
        setError('결제 위젯 로드에 실패했습니다.');
        setIsLoading(false);
      }
    };

    if (!clientKey) {
      setError('토스페이먼츠 클라이언트 키가 설정되지 않았습니다.');
      setIsLoading(false);
      return;
    }

    initPaymentWidget();
  
    return () => {
      try {
        if (paymentMethodsWidgetRef.current?.destroy) {
          paymentMethodsWidgetRef.current.destroy();
        }
      } catch (e) {}
      try {
        if (agreementWidgetRef.current?.destroy) {
          agreementWidgetRef.current.destroy();
        }
      } catch (e) {}
      paymentMethodsWidgetRef.current = null;
      agreementWidgetRef.current = null;
      widgetsRef.current = null;
      const pmEl = document.getElementById(pmId);
      if (pmEl) pmEl.innerHTML = '';
      const agEl = document.getElementById(agId);
      if (agEl) agEl.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientKey]);

  // 금액 변경 시 위젯 금액 업데이트 (v2 API)
  useEffect(() => {
    const updateAmount = async () => {
      if (!widgetsRef.current) return;
      try {
        await widgetsRef.current.setAmount({ currency: 'KRW', value: amount });
      } catch (err) {
        console.warn('결제 금액 업데이트 중 경고:', err);
      }
    };
    updateAmount();
  }, [amount]);

  const handlePayment = async () => {
    if (!widgetsRef.current) {
      onError('결제 위젯이 로드되지 않았습니다.');
      return;
    }

    try {
      if (isRequestingRef.current) {
        onError('결제가 진행 중입니다. 잠시만 기다려주세요.');
        return;
      }
      isRequestingRef.current = true;
      // 결제 요청 (공식 샘플 패턴)
      await widgetsRef.current.requestPayment({
        orderId: orderId,
        orderName: orderName,
        successUrl: `${window.location.origin}/payment-result?worship_id=${worshipId || ''}`,
        failUrl: `${window.location.origin}/payment-result?worship_id=${worshipId || ''}`,
      });
    } catch (error: any) {
      console.error('결제 요청 오류:', error);
      onError(error.message || '결제 요청 중 오류가 발생했습니다.');
    } finally {
      isRequestingRef.current = false;
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2, 
          py: 4 
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          결제 위젯을 로드 중입니다...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        결제 정보
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          금액: {amount.toLocaleString()}원
        </Typography>
        <Typography variant="body2" color="text.secondary">
          주문명: {orderName}
        </Typography>
      </Box>

      {/* 결제 수단 선택 */}
      <Box 
        id={paymentMethodsContainerIdRef.current}
        sx={{ 
          mb: 3,
          '& > div': {
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }
        }}
      />

      {/* 약관 동의 */}
      <Box 
        id={agreementContainerIdRef.current}
        sx={{ 
          mb: 3,
          '& > div': {
            borderRadius: 1
          }
        }}
      />

      {/* 결제 버튼 */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        onClick={handlePayment}
        sx={{ 
          py: 1.5,
          fontSize: '1.1rem',
          fontWeight: 'bold'
        }}
      >
        {amount.toLocaleString()}원 결제하기
      </Button>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>안전한 결제:</strong> 모든 결제 정보는 토스페이먼츠를 통해 암호화되어 안전하게 처리됩니다.
        </Typography>
      </Box>
    </Box>
  );
};

export default TossPaymentWidget;